import {
  BreakPointCondition,
  Button,
  Heading,
  Icon,
  Text,
  useBreakpointIf,
  HStack,
  Stack,
  VStack,
} from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { IStackProps, View } from 'native-base';
import React from 'react';
import { ScrollView, StyleSheet } from 'react-native';

import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { yearsQuery } from '../../store/managementAnalysis/queries';

import { ConversionChart } from './components/ConversionChart';
import { FinancialMovementChart } from './components/FinancialMovementChart';
import { InvestmentResume } from './components/InvestmentResume';

const styles = StyleSheet.create({
  scrollviewContainer: {
    flex: 1,
  },
});

const ChartHalfWidthContainer = (props: IStackProps) => (
  <Stack w={Utils.Responsive.TabletDesktop({ desktop: '49%', tablet: '100%' })} {...props} />
);

export const ManagementAnalysis = () => {
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');

  const {
    loading: yearsLoading,
    data: yearsData,
    error: yearsError,
    refresher: yearsRefresher,
  } = useRecoilQuery(yearsQuery);

  const handleReload = () => {
    yearsRefresher();
  };

  if (yearsError) {
    return (
      <VStack alignItems="center" paddingY={Spacing['stack.xxxl']} height="100%">
        <VStack alignItems="center" space={Spacing['stack.nano']}>
          <Icon
            name="Warning"
            testId="error-icon"
            color={Color['feedback.attention.default']}
            size="md"
          />
          <Text fontSize={Typography['fontsize.lg']} variant="semibold" color="neutral.black">
            Ops, Algo deu errado!!
          </Text>
          <Text fontSize={Typography['fontsize.md']} color="neutral.black">
            No momento não conseguimos te mostrar os resultados.
          </Text>
          <Button
            justifyContent="flex-start"
            size="lg"
            variant="textLink"
            rightIcon={
              <Icon
                name="Refresh"
                size="xxs"
                testId="aa"
                color={Color['brand.tertiary.principal']}
              />
            }
            onPress={handleReload}>
            Recarregar
          </Button>
        </VStack>
      </VStack>
    );
  }

  return (
    <ScrollView style={styles.scrollviewContainer}>
      <VStack
        py={Spacing['inline.xxxs']}
        px={Utils.Responsive.TabletDesktop({
          desktop: Spacing['inline.sm'],
          tablet: Spacing['inline.xxxs'],
        })}>
        <View width="100%" minW="container" flex={1}>
          <Heading testId="qa" size="h2" pb={Spacing['stack.xxxs']}>
            Análise gerencial
          </Heading>
          <VStack space={Spacing['stack.xxxs']} pb={Spacing['stack.sm']}>
            <Stack
              width="full"
              flexWrap="wrap"
              justifyContent="space-between"
              direction={Utils.Responsive.TabletDesktop({ desktop: 'row', tablet: 'column' })}>
              <ChartHalfWidthContainer
                mb={Utils.Responsive.TabletDesktop({ desktop: 0, tablet: Spacing['stack.xxxs'] })}>
                <ConversionChart
                  yearsLoading={yearsLoading}
                  yearsError={yearsError}
                  years={yearsData}
                  isMobile={isMobile}
                />
              </ChartHalfWidthContainer>
              <ChartHalfWidthContainer>
                <FinancialMovementChart
                  yearsLoading={yearsLoading}
                  yearsError={yearsError}
                  years={yearsData}
                  isMobile={isMobile}
                />
              </ChartHalfWidthContainer>
            </Stack>
            <HStack justifyContent="space-between">
              <InvestmentResume
                yearsLoading={yearsLoading}
                yearsError={yearsError}
                years={yearsData}
                isMobile={isMobile}
                alertText="Valores podem variar dependendo do intervalo de atualização das 9:00h às 19:00h"
              />
            </HStack>
          </VStack>
        </View>
      </VStack>
    </ScrollView>
  );
};
